import * as React from 'react';
import classNames from 'classnames';
import { store } from 'Models/Store';
import { Button, Display, Colors, Sizes } from '../Button/Button';
import If from '../If/If';

export interface IConfirmModalOptions {
	/** The text to be placed in the confirm button */
	confirmText?: string;
	/** The text to be placed in the cancel button */
	cancelText?: string;
	/** Class name on the modal */
	className?: string;
	/** Class name on the header div */
	headerClassName?: string;
	/** Class name on the message div */
	messageClassName?: string;
	/** Class name on the action div */
	actionClassName?: string;
	/** Class name for the modal overlay */
	overlayClassName?: string;
	/**
	 * If this option is set to true, when the cancel button is pressed the promise is resolved with a value of false
	 * instead being rejected.
	 */
	resolveOnCancel?: boolean;
	/** Boolean indicating if the overlay should close the modal. Defaults to true. */
	shouldCloseOnOverlayClick?: boolean;
	/** Boolean indicating if pressing the esc key should close the modal */
	shouldCloseOnEsc?: boolean;
	resolveOnClose?: boolean;
}

/**
 * Opens a confirm modal which gives a user an option to confirm or cancel
 * @param title The title on the modal
 * @param message The message to display in the modal
 * @param options Extra options for the modal
 * @returns A promise that is resolved on the user confirming or rejected on the user cancelling
 */
export function confirmModal(title: string, message: React.ReactNode, options: IConfirmModalOptions = {}) {
	return new Promise<boolean>((resolve, reject) => {
		const onConfirm = () => {
			store.modal.hide();
			resolve(true);
		};

		const onCancel = () => {
			store.modal.hide();
			if (options.resolveOnCancel) {
				resolve(false);
			} else {
				reject();
			}
		};

		const confirmText = options.confirmText ?? 'Confirm';
		const cancelText = options.cancelText ?? 'Cancel';
		const onClose = () => {
			store.modal.hide();
			reject();
		};

		const confirmDom = (
			<>
				<div key="header" className={classNames('modal__header', options.headerClassName)}>
					<h2 key="header-item" className="modal__title">
						{title}
					</h2>
					<Button
						key="header-cancel"
						className="modal--close"
						icon={{ icon: 'close', iconPos: 'icon-left' }}
						display={Display.Text}
						colors={Colors.Secondary}
						onClick={() => {
							return options.resolveOnClose ? onClose() : onCancel();
						}}
						labelVisible={false}
					>
						{cancelText}
					</Button>
				</div>
				<div key="message" className={classNames('modal__message', options.messageClassName)}>
					{message}
				</div>
				<div key="actions" className={classNames('modal__actions', options.actionClassName)}>
					<Button
						className="modal--cancel"
						key="cancel"
						onClick={onCancel}
						display={Display.Solid}
						colors={Colors.Secondary}
						sizes={Sizes.Medium}
					>
						{cancelText}
					</Button>
					<Button
						className="modal--confirm"
						key="confirm"
						onClick={onConfirm}
						display={Display.Solid}
						colors={Colors.Alternate}
						sizes={Sizes.Medium}
					>
						{confirmText}
					</Button>
				</div>
			</>
		);

		store.modal?.show(title, confirmDom, {
			className: classNames('confirm-modal', options.className),
			overlayClassName: options.overlayClassName,
			shouldCloseOnEsc: options.shouldCloseOnEsc,
			shouldCloseOnOverlayClick: options.shouldCloseOnOverlayClick,
			onRequestClose: onCancel,
		});
	});
}

export interface IAlertModalProps {
	/** The text to be placed in the cancel button */
	cancelText?: React.ReactNode;
	/** Class name on the modal */
	className?: string;
	/** Class name on the header div */
	headerClassName?: string;
	/** Class name on the message div */
	messageClassName?: string;
	/** Class name for the modal overlay */
	overlayClassName?: string;
	/** Boolean indicating if the overlay should close the modal. Defaults to true. */
	shouldCloseOnOverlayClick?: boolean;
	/** Boolean indicating if pressing the esc key should close the modal */
	shouldCloseOnEsc?: boolean;
	buttonColour?: Colors;
	/** A label for the modal for screen readers */
	label?: string;
}

/**
 * Displays an alert on the screen
 * @param title A title for the modal
 * @param message The message content for the modal
 * @param options Extra options for the modal
 * @returns A promise that is resolved when the modal is closed
 */
export function alertModal(title: React.ReactNode, message: React.ReactNode, options: IAlertModalProps = {}) {
	return new Promise<void>(resolve => {
		const onClose = () => {
			store.modal.hide();
			resolve();
		};

		const cancelText = options.cancelText ?? 'Cancel';
		const { shouldCloseOnEsc = true, shouldCloseOnOverlayClick = true } = options;

		const alertDom = (
			<>
				<div key="header" className={classNames('modal__header', options.headerClassName)}>
					<h2 key="header-item" className="modal__title">
						{title}
					</h2>
					<If condition={shouldCloseOnOverlayClick || shouldCloseOnEsc}>
						<Button
							key="header-cancel"
							icon={{ icon: 'close', iconPos: 'icon-left' }}
							display={Display.Text}
							onClick={onClose}
							labelVisible={false}
						/>
					</If>
				</div>
				<div key="message" className={classNames('modal__message', options.messageClassName)}>
					{message}
				</div>
				<div className="modal__actions">
					<Button
						key="action-cancel"
						className="btn btn--solid btn--alternate modal--cancel"
						onClick={onClose}
						colors={options.buttonColour ?? Colors.Secondary}
						icon={{ icon: 'arrow-right', iconPos: 'icon-right' }}
					>
						{cancelText}
					</Button>
				</div>
			</>
		);

		const label = options.label ?? (typeof title === 'string' ? title : 'Modal');

		store.modal.show(label, alertDom, {
			className: classNames('alert-modal', options.className),
			overlayClassName: options.overlayClassName,
			onRequestClose: onClose,
			shouldCloseOnEsc: options.shouldCloseOnEsc,
			shouldCloseOnOverlayClick: options.shouldCloseOnOverlayClick,
		});
	});
}
